import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Button, IconButton, Dialog, DialogTitle,
    DialogContent, DialogActions, TextField, CircularProgress, Snackbar, Alert,
    Paper, Stack, Checkbox, FormControlLabel, Chip, Backdrop, useTheme,
    TableContainer, Table, TableHead, TableBody, TableRow, TableCell, Tooltip
} from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import Lottie from 'lottie-react';
import { format, parseISO, isValid } from 'date-fns';

// Icons
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import PrintRoundedIcon from '@mui/icons-material/PrintRounded';
import EventRoundedIcon from '@mui/icons-material/EventRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';

// Animations (Same as SendMessage.js)
import successAnimation from '../../assets/icons/sucess.json';
import loadingAnimation from '../../assets/icons/message-sending.json'; 

import axiosInstance from '../../api/axiosConfig';
import NoResultsImage from '../../assets/no-results.png';
import { STORAGE_KEYS } from '../../constants';
import AdminPageHero from '../common/AdminPageHero';
import AdminPanelCard from '../common/AdminPanelCard';
import PageContainer from '../common/PageContainer';
import EmptyState from '../common/EmptyState';
import {
    buildReportHtml,
    downloadTablePdf,
    escapeHtml,
    getSchoolReportProfile,
    openPrintPreview,
} from '../../utils/reportPrintUtils';

// --- STYLED COMPONENTS (Aligned with SendMessage.js) ---

const ListSurface = styled(AdminPanelCard)(({ theme }) => ({
    padding: theme.spacing(2),
    borderRadius: 6,
    background: 'linear-gradient(180deg, #ffffff 0%, #fbfcff 100%)',
}));

const SoftTableContainer = styled(TableContainer)(({ theme }) => ({
    borderRadius: 6,
    border: 'none',
    backgroundColor: '#ffffff',
    overflow: 'hidden',
    '& .MuiTable-root': {
        borderCollapse: 'separate',
        borderSpacing: 0,
    },
    '& .MuiTableHead-root .MuiTableRow-root': {
        backgroundColor: '#f5f7fb',
    },
    '& .MuiTableHead-root .MuiTableCell-root': {
        borderBottom: '1px solid rgba(148, 163, 184, 0.18)',
        color: '#64748b',
        fontSize: '0.78rem',
        fontWeight: 700,
        letterSpacing: '0.02em',
        textTransform: 'uppercase',
        backgroundColor: '#f5f7fb',
    },
    '& .MuiTableBody-root .MuiTableRow-root': {
        backgroundColor: '#ffffff',
        transition: 'background-color 0.2s ease',
    },
    '& .MuiTableBody-root .MuiTableRow-root:hover': {
        backgroundColor: '#f8fbff',
    },
    '& .MuiTableBody-root .MuiTableRow-root:last-of-type .MuiTableCell-root': {
        borderBottom: 'none',
    },
    '& .MuiTableBody-root .MuiTableCell-root': {
        borderBottom: '1px solid rgba(226, 232, 240, 0.75)',
        borderLeft: 'none',
        borderRight: 'none',
        fontSize: '0.92rem',
        color: theme.palette.text.primary,
        paddingTop: theme.spacing(1.6),
        paddingBottom: theme.spacing(1.6),
    },
}));

// --- MAIN COMPONENT ---

const HolidaysManager = () => {
    const theme = useTheme();
    const schoolId = localStorage.getItem(STORAGE_KEYS.SCHOOL_ID);
    
    const [holidays, setHolidays] = useState([]);
    const [loading, setLoading] = useState(false);
    
    const [formDialogOpen, setFormDialogOpen] = useState(false);
    const [formData, setFormData] = useState({
        id: null, name: '', startDate: null, endDate: null, description: '', multipleDays: false,
    });
    const[formErrors, setFormErrors] = useState({});
    
    const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);
    const [holidayToDelete, setHolidayToDelete] = useState(null);
    
    // Aligned with SendMessage.js animation states
    const [animationState, setAnimationState] = useState('idle'); 
    
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [sortDirection, setSortDirection] = useState('asc');

    const email = localStorage.getItem(STORAGE_KEYS.SCHOOL_EMAIL);
    const phone = localStorage.getItem(STORAGE_KEYS.SCHOOL_PHONE);

    let contact = 'Contact Unavailable';
    if (email && phone) contact = `${email} | ${phone}`;
    else if (email) contact = email;
    else if (phone) contact = phone;

    const schoolDetails = {
        name: localStorage.getItem('schoolName') || 'School Name Unavailable',
        address: localStorage.getItem(STORAGE_KEYS.SCHOOL_ADDRESS) || 'Address Unavailable',
        contact: contact,
    };

    useEffect(() => {
        if (schoolId) fetchHolidays();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    },[schoolId, sortDirection]);

    const fetchHolidays = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get(`/api/holidays/all`);
            const holidaysData = response.data ||[];

            const holidaysWithStatus = holidaysData.map(h => ({
                ...h,
                isCompleted: h.end_date ? new Date(h.end_date) < new Date() : (h.start_date ? new Date(h.start_date) < new Date() : false)
            }));

            const sortedHolidays = holidaysWithStatus.sort((a, b) => {
                const dateA = a.start_date ? parseISO(a.start_date) : new Date(0);
                const dateB = b.start_date ? parseISO(b.start_date) : new Date(0);
                if (!isValid(dateA)) return 1;
                if (!isValid(dateB)) return -1;
                return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
            });
            setHolidays(sortedHolidays);
        } catch (error) {
            setSnackbar({ open: true, message: 'Error fetching holidays', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleOpenFormDialog = (holiday = null) => {
        if (holiday) {
            const startDate = holiday.start_date && isValid(parseISO(holiday.start_date)) ? parseISO(holiday.start_date) : null;
            const endDate = holiday.end_date && isValid(parseISO(holiday.end_date)) ? parseISO(holiday.end_date) : null;
            const multipleDays = !!startDate && !!endDate && startDate.getTime() !== endDate.getTime();

            setFormData({
                id: holiday.id,
                name: holiday.name || '',
                startDate: startDate,
                endDate: multipleDays ? endDate : null,
                description: holiday.description || '',
                multipleDays: multipleDays,
            });
        } else {
            setFormData({ id: null, name: '', startDate: null, endDate: null, description: '', multipleDays: false });
        }
        setFormErrors({});
        setFormDialogOpen(true);
    };

    const handleFormSubmit = async () => {
        const { id, name, startDate, endDate, description, multipleDays } = formData;
        const errors = {};

        if (!name.trim()) errors.name = 'Holiday Name is required';
        if (!startDate || !isValid(startDate)) errors.startDate = 'Valid From Date is required';
        if (multipleDays && (!endDate || !isValid(endDate))) errors.endDate = 'Valid To Date is required';
        if (multipleDays && startDate && endDate && startDate > endDate) errors.endDate = 'To Date cannot be before From Date';

        setFormErrors(errors);
        if (Object.keys(errors).length > 0) return;

        const finalEndDate = multipleDays ? (endDate ? format(endDate, 'yyyy-MM-dd') : null) : (startDate ? format(startDate, 'yyyy-MM-dd') : null);
        const payload = { name: name.trim(), start_date: startDate ? format(startDate, 'yyyy-MM-dd') : null, end_date: finalEndDate, description: description.trim(), schoolId };

        setFormDialogOpen(false);
        setAnimationState('saving');
        
        try {
            if (id) {
                const response = await axiosInstance.put(`/api/holidays/${id}`, payload);
                setHolidays(prev => prev.map(h => (h.id === id ? { ...response.data, isCompleted: checkCompleted(response.data) } : h)).sort(sortHolidays));
            } else {
                const response = await axiosInstance.post('/api/holidays', payload);
                setHolidays(prev =>[...prev, { ...response.data, isCompleted: checkCompleted(response.data) }].sort(sortHolidays));
            }
            
            setAnimationState('success');
            setTimeout(() => {
                setSnackbar({ open: true, message: id ? 'Holiday updated successfully!' : 'Holiday added successfully!', severity: 'success' });
                setAnimationState('idle');
            }, 2000);

        } catch (error) {
            setAnimationState('error');
            setSnackbar({ open: true, message: error.response?.data?.message || 'Error saving holiday', severity: 'error' });
        }
    };

    const handleConfirmDelete = async () => {
        if (!holidayToDelete) return;
        setConfirmDeleteDialogOpen(false);
        setAnimationState('saving');

        try {
            await axiosInstance.delete(`/api/holidays/${holidayToDelete.id}`);
            setHolidays(prev => prev.filter(h => h.id !== holidayToDelete.id));
            
            setAnimationState('success');
            setTimeout(() => {
                setSnackbar({ open: true, message: 'Holiday deleted successfully', severity: 'success' });
                setAnimationState('idle');
                setHolidayToDelete(null);
            }, 2000);
        } catch (error) {
            setAnimationState('error');
            setSnackbar({ open: true, message: error.response?.data?.message || 'Error deleting holiday', severity: 'error' });
        }
    };

    const formatDate = (dateStr) => {
        try {
            const date = parseISO(dateStr);
            return isValid(date) ? format(date, 'dd MMM yyyy') : "Invalid Date";
        } catch { return "Invalid Date"; }
    };

    const getDayName = (dateStr) => {
        try {
            const date = parseISO(dateStr);
            return isValid(date) ? format(date, 'EEE') : "";
        } catch { return ""; }
    };

    const checkCompleted = (holiday) => {
        const dateToCheck = holiday.end_date || holiday.start_date;
        return dateToCheck ? new Date(dateToCheck) < new Date(new Date().setHours(0, 0, 0, 0)) : false;
    };

    const sortHolidays = (a, b) => {
        const dateA = a.start_date ? parseISO(a.start_date) : new Date(0);
        const dateB = b.start_date ? parseISO(b.start_date) : new Date(0);
        if (!isValid(dateA)) return 1;
        if (!isValid(dateB)) return -1;
        return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
    };

    const handlePrint = () => {
        try {
            const cards = [
                `<div class="meta-card"><div class="label">Total Holidays</div><div class="value">${holidays.length}</div></div>`,
                `<div class="meta-card"><div class="label">Upcoming</div><div class="value">${holidays.filter((holiday) => !holiday.isCompleted).length}</div></div>`,
                `<div class="meta-card"><div class="label">Completed</div><div class="value">${holidays.filter((holiday) => holiday.isCompleted).length}</div></div>`,
            ].join('');

            const rows = holidays.length > 0
                ? holidays.map((holiday, index) => `
                    <tr>
                        <td>${index + 1}</td>
                        <td>${escapeHtml(formatDate(holiday.start_date))}${holiday.start_date !== holiday.end_date ? `<br/><span style="color:#64748b;">to ${escapeHtml(formatDate(holiday.end_date))}</span>` : ''}</td>
                        <td>${escapeHtml(getDayName(holiday.start_date))}</td>
                        <td><strong>${escapeHtml(holiday.name)}</strong><br/><span style="color:#64748b;">${escapeHtml(holiday.description || 'No description')}</span></td>
                        <td>${holiday.isCompleted ? 'Completed' : 'Upcoming'}</td>
                    </tr>
                `).join('')
                : `<tr><td colspan="5">No holidays mapped yet.</td></tr>`;

            openPrintPreview({
                title: 'Holiday register print',
                html: buildReportHtml({
                    title: 'Academic Holiday Register',
                    subtitle: 'Printable school holiday calendar',
                    school: {
                        schoolName: schoolDetails.name,
                        address: schoolDetails.address,
                        contact: schoolDetails.contact,
                    },
                    body: `
                        <div class="meta-grid">${cards}</div>
                        <table>
                            <thead>
                                <tr>
                                    <th>No.</th>
                                    <th>Date</th>
                                    <th>Day</th>
                                    <th>Holiday Details</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>${rows}</tbody>
                        </table>
                    `,
                }),
            });
        } catch (error) {
            setSnackbar({ open: true, message: error.message || 'Failed to initiate print.', severity: 'error' });
        }
    };

    const handleDownloadPdf = () => {
        downloadTablePdf({
            title: 'Academic Holiday Register',
            subtitle: 'Printable and downloadable holiday calendar',
            filename: `holiday_register_${format(new Date(), 'yyyyMMdd')}.pdf`,
            columns: ['#', 'Date', 'Day', 'Holiday', 'Status'],
            rows: holidays.map((holiday, index) => [
                index + 1,
                holiday.start_date !== holiday.end_date
                    ? `${formatDate(holiday.start_date)} to ${formatDate(holiday.end_date)}`
                    : formatDate(holiday.start_date),
                getDayName(holiday.start_date),
                `${holiday.name}${holiday.description ? ` - ${holiday.description}` : ''}`,
                holiday.isCompleted ? 'Completed' : 'Upcoming',
            ]),
            school: {
                schoolName: schoolDetails.name,
                address: schoolDetails.address,
                contact: schoolDetails.contact,
                logoUrl: getSchoolReportProfile().logoUrl,
            },
        });
        setSnackbar({ open: true, message: 'Holiday register downloaded as PDF.', severity: 'success' });
    };

    return (
        <PageContainer>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
                
                <AdminPageHero
                    title="Holiday Planner"
                    subtitle="Manage school holidays, long breaks, and printable holiday schedules."
                    metrics={[
                        { label: 'Total', value: holidays.length },
                        { label: 'Upcoming', value: holidays.filter((h) => !h.isCompleted).length },
                        { label: 'Completed', value: holidays.filter((h) => h.isCompleted).length },
                    ]}
                    actions={(
                        <Stack direction="row" alignItems="center" spacing={1.2} flexWrap="wrap" useFlexGap>
                            <Button 
                                variant="outlined" color="inherit" 
                                startIcon={<PrintRoundedIcon />} onClick={handlePrint} 
                                disabled={loading || holidays.length === 0}
                                sx={{ borderRadius: 1.5, fontWeight: 700, borderColor: alpha(theme.palette.divider, 0.8) }}
                            >
                                Print List
                            </Button>
                            <Button
                                variant="outlined" color="inherit"
                                startIcon={<PrintRoundedIcon />} onClick={handleDownloadPdf}
                                disabled={loading || holidays.length === 0}
                                sx={{ borderRadius: 1.5, fontWeight: 700, borderColor: alpha(theme.palette.divider, 0.8) }}
                            >
                                Download PDF
                            </Button>
                            <Button 
                                variant="contained" color="primary" 
                                startIcon={<AddRoundedIcon />} onClick={() => handleOpenFormDialog()}
                                sx={{ py: 1, borderRadius: 1.5, fontWeight: 800 }}
                            >
                                Add Holiday
                            </Button>
                        </Stack>
                    )}
                />

                {/* LIST CONTENT */}
                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" height="40vh">
                        <CircularProgress />
                    </Box>
                ) : holidays.length > 0 ? (
                    <ListSurface
                        title="Holiday Calendar"
                        subtitle="Review school holidays, update dates, and maintain the academic calendar."
                        contentSx={{ p: 0 }}
                        sx={{ mb: 4 }}
                    >
                        <SoftTableContainer>
                        <Table sx={{ minWidth: 980 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ width: 56 }}>#</TableCell>
                                    <TableCell>Holiday Name</TableCell>
                                    <TableCell>From Date</TableCell>
                                    <TableCell>To Date</TableCell>
                                    <TableCell>Day</TableCell>
                                    <TableCell>Description</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {holidays.map((holiday, index) => {
                                    const isCompleted = holiday.isCompleted;
                                    const descriptionText = holiday.description?.trim() || '-';
                                    const isMultiDay = holiday.start_date && holiday.end_date && holiday.start_date !== holiday.end_date;

                                    return (
                                        <TableRow key={holiday.id} hover>
                                            <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>
                                                {index + 1}
                                            </TableCell>
                                            <TableCell>
                                                <Stack spacing={0.75}>
                                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                        {holiday.name}
                                                    </Typography>
                                                    <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
                                                        {isMultiDay && <Chip size="small" variant="outlined" label="Multi-day" />}
                                                    </Stack>
                                                </Stack>
                                            </TableCell>
                                            <TableCell>{formatDate(holiday.start_date)}</TableCell>
                                            <TableCell>{formatDate(holiday.end_date || holiday.start_date)}</TableCell>
                                            <TableCell>{getDayName(holiday.start_date)}</TableCell>
                                            <TableCell sx={{ maxWidth: 320 }}>
                                                <Tooltip title={descriptionText === '-' ? '' : descriptionText}>
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 2,
                                                            WebkitBoxOrient: 'vertical',
                                                            overflow: 'hidden',
                                                            whiteSpace: 'normal',
                                                        }}
                                                    >
                                                        {descriptionText}
                                                    </Typography>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    size="small"
                                                    variant="outlined"
                                                    label={isCompleted ? 'Completed' : 'Upcoming'}
                                                    sx={{
                                                        minWidth: 86,
                                                        borderColor: isCompleted
                                                            ? alpha(theme.palette.text.secondary, 0.3)
                                                            : alpha(theme.palette.success.main, 0.35),
                                                        color: isCompleted ? 'text.secondary' : theme.palette.success.main,
                                                        backgroundColor: isCompleted
                                                            ? alpha(theme.palette.text.secondary, 0.06)
                                                            : alpha(theme.palette.success.main, 0.08),
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                                <Tooltip title="Edit">
                                                    <IconButton size="small" onClick={() => handleOpenFormDialog(holiday)}>
                                                        <EditRoundedIcon fontSize="small" color="action" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete">
                                                    <IconButton size="small" onClick={() => { setHolidayToDelete(holiday); setConfirmDeleteDialogOpen(true); }}>
                                                        <DeleteRoundedIcon fontSize="small" color="error" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                        </SoftTableContainer>
                    </ListSurface>
                ) : (
                    <EmptyState
                        title="No holidays added yet"
                        description="Add your first holiday to generate the academic holiday calendar."
                        icon={<img src={NoResultsImage} alt="No Holidays" style={{ maxWidth: '160px', opacity: 0.8 }} />}
                        action={(
                            <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => handleOpenFormDialog()} sx={{ mt: 2, borderRadius: 1.5, fontWeight: 800, px: 3 }}>
                                Add First Holiday
                            </Button>
                        )}
                    />
                )}

                {/* ADD/EDIT FORM DIALOG */}
                <Dialog open={formDialogOpen} onClose={() => setFormDialogOpen(false)} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 4, p: 1 } }}>
                    <DialogTitle sx={{ fontWeight: 800, textAlign: 'center', pb: 1 }}>
                        {formData.id ? 'Edit Holiday Details' : 'Add New Holiday'}
                    </DialogTitle>
                    <DialogContent>
                        <Stack spacing={3} sx={{ pt: 2 }}>
                            <TextField
                                label="Holiday Name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                fullWidth required error={!!formErrors.name} helperText={formErrors.name}
                                InputProps={{ sx: { borderRadius: 2 } }}
                            />
                            
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                <DatePicker
                                    label="From Date *" value={formData.startDate} inputFormat="dd MMM yyyy"
                                    onChange={(val) => setFormData({ ...formData, startDate: val, endDate: !formData.multipleDays ? val : formData.endDate })}
                                    renderInput={(params) => <TextField {...params} fullWidth error={!!formErrors.startDate} helperText={formErrors.startDate} InputProps={{ ...params.InputProps, sx: { borderRadius: 2 } }} />}
                                />
                                {formData.multipleDays && (
                                     <DatePicker
                                        label="To Date *" value={formData.endDate} inputFormat="dd MMM yyyy" minDate={formData.startDate}
                                        onChange={(val) => setFormData({ ...formData, endDate: val })}
                                        renderInput={(params) => <TextField {...params} fullWidth error={!!formErrors.endDate} helperText={formErrors.endDate} InputProps={{ ...params.InputProps, sx: { borderRadius: 2 } }} />}
                                    />
                                )}
                            </Stack>

                            <FormControlLabel
                                control={<Checkbox checked={formData.multipleDays} onChange={(e) => setFormData({ ...formData, multipleDays: e.target.checked, endDate: !e.target.checked ? formData.startDate : formData.endDate })} />}
                                label={<Typography variant="body2" fontWeight={600}>This holiday spans across multiple days</Typography>}
                            />

                            <TextField
                                label="Description (Optional)" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                fullWidth multiline rows={3} placeholder="E.g. School will remain closed for Diwali."
                                InputProps={{ sx: { borderRadius: 2 } }}
                            />
                        </Stack>
                    </DialogContent>
                    <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
                        <Button onClick={() => setFormDialogOpen(false)} color="inherit" sx={{ fontWeight: 700 }}>Cancel</Button>
                        <Button variant="contained" onClick={handleFormSubmit} sx={{ px: 4, borderRadius: 2, fontWeight: 700 }}>
                            {formData.id ? 'Save Changes' : 'Create Holiday'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* DELETE CONFIRMATION DIALOG */}
                <Dialog open={confirmDeleteDialogOpen} onClose={() => setConfirmDeleteDialogOpen(false)} PaperProps={{ sx: { borderRadius: 4, p: 1, minWidth: 400 } }}>
                    <DialogTitle sx={{ fontWeight: 800, textAlign: 'center', pb: 1 }}>Confirm Deletion</DialogTitle>
                    <DialogContent sx={{ textAlign: 'center' }}>
                        <Typography variant="body1">
                            Are you sure you want to delete <strong>"{holidayToDelete?.name}"</strong>? This action cannot be undone.
                        </Typography>
                    </DialogContent>
                    <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
                        <Button onClick={() => setConfirmDeleteDialogOpen(false)} color="inherit" sx={{ fontWeight: 700 }}>Cancel</Button>
                        <Button onClick={handleConfirmDelete} variant="contained" color="error" sx={{ px: 4, borderRadius: 2, fontWeight: 700 }}>
                            Yes, Delete
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* FULLSCREEN ANIMATION OVERLAY (Aligned with SendMessage.js) */}
                <Backdrop open={animationState !== 'idle'} sx={{ zIndex: 9999, flexDirection: 'column', bgcolor: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(8px)' }}>
                    <Box sx={{ textAlign: 'center' }}>
                        {animationState === 'saving' && (
                            <>
                                <Lottie animationData={loadingAnimation} style={{ width: 250, height: 250, margin: '0 auto' }} />
                                <Typography variant="h5" fontWeight={800} color="primary" mt={-2}>Processing...</Typography>
                            </>
                        )}
                        {animationState === 'success' && (
                            <>
                                <Lottie animationData={successAnimation} loop={false} style={{ width: 250, height: 250, margin: '0 auto' }} />
                                <Typography variant="h5" fontWeight={800} color="success.main" mt={-2}>Success!</Typography>
                            </>
                        )}
                        {animationState === 'error' && (
                            <>
                                <ErrorRoundedIcon color="error" sx={{ fontSize: 100, mb: 2 }} />
                                <Typography variant="h5" fontWeight={800} color="error.main">Action Failed</Typography>
                                <Typography variant="body1" color="text.secondary" mt={1} mb={3}>{snackbar.message}</Typography>
                                <Button variant="contained" color="primary" onClick={() => setAnimationState('idle')} sx={{ borderRadius: 2, fontWeight: 700 }}>Dismiss</Button>
                            </>
                        )}
                    </Box>
                </Backdrop>

                {/* SNACKBAR */}
                <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({...s, open: false}))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                    <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: 1.5, fontWeight: 600 }}>{snackbar.message}</Alert>
                </Snackbar>

            </LocalizationProvider>
        </PageContainer>
    );
};

export default HolidaysManager;
